import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import useTokenValidation from 'hooks/useTokenValidation';
import metasComerciaisService from 'features/comercial/services/metasComerciaisService';
import useComercialConsolidado from 'features/comercial/hooks/useComercialConsolidado';
import { LoadingSpinner, CompanyChangeBlur } from 'components/common';
import PageLayout from 'components/common/PageLayout';
import { ChevronLeft, ChevronRight, Settings, Trophy, LayoutDashboard, Target, DollarSign, ReceiptText, UserSearch } from 'lucide-react';
import { setDefaultCompanyContext, hasCompanyContext, getCompanyContext } from 'utils/setCompanyContext';
import { DateRangeFilterBlur } from 'components/filters';

// Componentes existentes reutilizados
import ListaInadimplenciaVendedores from 'features/comercial/components/ListaInadimplenciaVendedores';
import ListaClientesOsAberta72h from 'features/comercial/components/ListaClientesOsAberta72h';

// Novos componentes criados
import MetricasComerciais from 'features/comercial/components/MetricasComerciais';
import GraficoEvolucaoVendas from 'features/comercial/components/GraficoEvolucaoVendas';
import GraficoEvolucaoConsolidado from 'features/comercial/components/GraficoEvolucaoConsolidado';
import GraficoReceita from 'features/comercial/components/GraficoReceita';
import GestaoMetas from 'features/comercial/components/GestaoMetas';
import ModalMeta from 'features/comercial/components/ModalMeta';
import ComissaoTab from 'features/comercial/components/ComissaoTab';
import useCompanyContextKey from 'hooks/useCompanyContextKey';
import RankingComercialTab from 'features/comercial/components/RankingComercialTab';
import MinhasVendasTab from 'features/comercial/components/MinhasVendasTab';
import RitmoDaEquipe from 'features/comercial/components/RitmoDaEquipe';
import ConsultaXt from 'features/comercial/components/ConsultaXt';
import comissaoReguaService from 'features/comercial/services/comissaoReguaService';
import { obterEmpresaConsultada } from 'features/comercial/utils/consultaInterempresa';


const Comercial = () => {
  // Validação automática de token
  useTokenValidation();
  const { user } = useAuth();
  
  // Definir contexto da empresa se não existir
  useEffect(() => {
    if (!hasCompanyContext()) {
      setDefaultCompanyContext();
    }
  }, []);
  
  // Verificar se é modo consolidado
  const companyContext = getCompanyContext();
  const isConsolidated = companyContext?.isGroup || companyContext?.isConsolidated;
  const empresaConsultada = obterEmpresaConsultada(companyContext);
  const userPermissions = user?.permissions || [];
  const hasPermission = (permissionName) => userPermissions.some((permission) => permission.name === permissionName);
  const hasComercialFull = hasPermission('comercial.view');
  const hasRankingOnly = hasPermission('comercial.ranking.view') && !hasComercialFull;
  // A vendedora tem só esta permissão: a página abre direto nas vendas dela, sem abas.
  const hasMinhasVendasOnly = hasPermission('comercial.minhas_vendas.view') && !hasComercialFull;
  
  // Estado para filtro de datas (padrão: "Até agora (mês)")
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  
  const [filtroData, setFiltroData] = useState({
    start: toISO(startOfMonth),
    end: toISO(today),
    presetId: 'mtd' // "Até agora (mês)"
  });
  
  // Hook para dados comerciais (individual e consolidado)
  const {
    dadosComerciais: dadosComerciaisHook,
    loading: loadingComercial,
    initialLoading,
    error: errorComercial,

    atualizarDados: atualizarDadosComerciais
  } = useComercialConsolidado(filtroData, { enabled: hasComercialFull });
  
  const [dadosComerciais, setDadosComerciais] = useState({
    totalVendas: 0,
    projecaoTotalVendas: null,
    totalReceitaVendas: 0,
    projecaoReceitaVendas: null,
    ticketMedio: 0,
    performanceMeta: 0,
    performanceReceita: 0,
    novosClientes: 0,
    conversao: 0
  });
  const [listaInadimplencia, setListaInadimplencia] = useState([]);
  const [clientesOsAberta72h, setClientesOsAberta72h] = useState([]);
  const [clientesAssinatura, setClientesAssinatura] = useState([]);
  const [, setUltimaAtualizacao] = useState(null);

  // Novos estados para sistema de metas
  const [abaAtiva, setAbaAtiva] = useState('dashboard');
  // A consulta é cruzada: MOV consulta XT, XT consulta MOV e a MOV Fit não oferece a aba.
  const [abaDaVendedora, setAbaDaVendedora] = useState('vendas');
  // `null` enquanto não se sabe; string 'YYYY-MM' quando há vigência cadastrada.
  const [inicioVigencia, setInicioVigencia] = useState(null);
  const [metasDefinidas, setMetasDefinidas] = useState({});
  const [, setLoadingMetas] = useState(false);
  const [modalMetaAberto, setModalMetaAberto] = useState(false);
  const [metaTemp, setMetaTemp] = useState('');
  const [mesEdicao, setMesEdicao] = useState('');
  const [anoEdicao, setAnoEdicao] = useState(new Date().getFullYear());
  const [periodoEdicaoLabel, setPeriodoEdicaoLabel] = useState('');
  const [vendedoresDisponiveisMetas, setVendedoresDisponiveisMetas] = useState([]);
  const [loadingVendedoresMetas, setLoadingVendedoresMetas] = useState(false);
  const [metasVendedoresTemp, setMetasVendedoresTemp] = useState([]);

  const empresaSelecionada = useCompanyContextKey();

  useEffect(() => {
    let mounted = true;
    comissaoReguaService.configuracao()
      .then((config) => {
        const inicio = config?.inicio_vigencia ? String(config.inicio_vigencia).slice(0, 7) : null;
        if (mounted) setInicioVigencia(inicio);
      })
      .catch(() => { if (mounted) setInicioVigencia(null); });
    return () => { mounted = false; };
    // `companyContext?.id` não existe no contexto gravado — o que há é `ids`, um array. A
    // dependência era `undefined` fixo e o efeito nunca refazia a busca ao trocar de empresa.
  }, [empresaSelecionada]);

  // Existe vigência cadastrada. A partir daqui a aba Comissão tem seletor de competência próprio, e
  // o filtro global de período deixa de ter efeito ali.
  //
  // Havia uma segunda variável, `novaReguaAtiva` ("a vigência já começou"), que servia só para
  // ESCONDER a aba Metas. A aba não some mais: ela é a única tela que mostra o histórico anterior à
  // vigência, e esse histórico continua válido. Quem decide o que é editável é a competência de
  // cada card, dentro de `GestaoMetas` — mês a mês, e não a aba inteira.
  const reguaConfigurada = !!inicioVigencia;

  // Na aba Comissão sob a régua o período vem da COMPETÊNCIA, não deste filtro. Mantê-lo visível
  // seria um controle que parece ativo e não muda nada — o pior tipo de affordance falsa.
  // A aba de vendas por vendedor também tem o seletor de mês dela: a competência da régua.
  const mostrarFiltroPeriodo = !(abaAtiva === 'comissao' && reguaConfigurada) && abaAtiva !== 'minhas-vendas' && abaAtiva !== 'consulta-interempresa';

  useEffect(() => {
    if (empresaConsultada) return;
    if (abaAtiva === 'consulta-interempresa') setAbaAtiva('dashboard');
    if (abaDaVendedora === 'consulta-interempresa') setAbaDaVendedora('vendas');
  }, [abaAtiva, abaDaVendedora, empresaConsultada]);


  // Estados para carrossel de gráficos
  const [currentChartIndex, setCurrentChartIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);

  // Estados para abas das listas
  const [activeTab, setActiveTab] = useState('vendedores');

  // Dados de evolução de vendas (serão carregados do N8N)
  const [dadosEvolucao, setDadosEvolucao] = useState([]);

  const getPeriodoMensal = useCallback((item) => {
    const meses = {
      '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
      '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
      '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez',
    };
    const mesesPorNome = {
      Jan: '01', Fev: '02', Mar: '03', Abr: '04',
      Mai: '05', Jun: '06', Jul: '07', Ago: '08',
      Set: '09', Out: '10', Nov: '11', Dez: '12',
    };
    const rawMes = item?.mes;
    const anoAtual = new Date().getFullYear();
    let mesFormatado = 'N/A';
    let ano = Number(item?.ano || item?.year) || anoAtual;

    if (typeof rawMes === 'string' && rawMes.includes('-')) {
      const partes = rawMes.split('-');
      const anoDoMes = Number(partes[0]);
      const mesNumero = partes[1];

      if (anoDoMes) ano = anoDoMes;
      mesFormatado = meses[mesNumero] || rawMes;
    } else if (typeof rawMes === 'string' && rawMes.length === 3) {
      mesFormatado = rawMes;
    } else if (typeof rawMes === 'number') {
      mesFormatado = meses[String(rawMes).padStart(2, '0')] || String(rawMes);
    } else if (rawMes) {
      mesFormatado = rawMes;
    }

    const periodoKey = `${ano}-${mesFormatado}`;
    const mesNumero = mesesPorNome[mesFormatado] || null;

    return {
      mes: mesFormatado,
      ano,
      mesNumero,
      periodoKey,
      periodoLabel: `${String(mesFormatado).toUpperCase()}/${ano}`,
    };
  }, []);

  const normalizarMetasPorAno = useCallback((metas, ano) => {
    const metasNormalizadas = {};

    Object.entries(metas || {}).forEach(([mes, meta]) => {
      metasNormalizadas[mes] = meta;
      metasNormalizadas[`${ano}-${mes}`] = meta;
    });

    return metasNormalizadas;
  }, []);

  const getMetaPeriodo = useCallback((item) => {
    if (!item) return null;
    return item.periodoKey
      ? metasDefinidas[item.periodoKey] || null
      : metasDefinidas[item.mes] || null;
  }, [metasDefinidas]);

  const normalizarMetasVendedores = useCallback((vendedoresMeta = []) => (vendedoresMeta || []).map((item) => ({
    vendedor_id: item.vendedor_id || item.id || '',
    vendedor_nome: item.vendedor_nome || item.nome || '',
    meta_vendas: item.meta_vendas ?? '',
  })), []);

  // Processar dados do hook (individual ou consolidado)
  const processarDadosComerciais = useCallback(() => {
    if (!dadosComerciaisHook) {
      return;
    }

    // Processar resumo comercial
    if (dadosComerciaisHook.resumo) {
      setDadosComerciais({
        totalVendas: dadosComerciaisHook.resumo.total_vendas || 0,
        // `?? null` e não `|| 0`: o backend devolve `null` quando o período está fechado ou é
        // futuro, e é isso que faz o card de projeção sumir. Com `|| 0` a tela mostraria uma
        // projeção zerada — que afirma outra coisa.
        projecaoTotalVendas: dadosComerciaisHook.resumo.projecao_total_vendas ?? null,
        totalReceitaVendas: dadosComerciaisHook.resumo.total_receita_vendas || 0,
        projecaoReceitaVendas: dadosComerciaisHook.resumo.projecao_receita_vendas ?? null,
        ticketMedio: dadosComerciaisHook.resumo.ticket_medio || 0,
        performanceMeta: 142, // Manter valores fixos por enquanto
        performanceReceita: 134,
        novosClientes: dadosComerciaisHook.resumo.total_vendas || 0,
        conversao: 89.4
      });
    } else {
    }

    // Processar inadimplência por vendedor
    if (dadosComerciaisHook.lista_inadimplencia && Array.isArray(dadosComerciaisHook.lista_inadimplencia)) {
      setListaInadimplencia(dadosComerciaisHook.lista_inadimplencia);
    } else {
      setListaInadimplencia([]);
    }

    // Processar clientes com OS aberta >72h
    const clientesOs72h = dadosComerciaisHook.clientes_os_aberta_72h || dadosComerciaisHook.novos_clientes;
    if (Array.isArray(clientesOs72h)) {
      setClientesOsAberta72h(clientesOs72h);
    } else {
      setClientesOsAberta72h([]);
    }

    if (Array.isArray(dadosComerciaisHook.clientes_assinatura)) {
      setClientesAssinatura(dadosComerciaisHook.clientes_assinatura);
    } else {
      setClientesAssinatura([]);
    }

    // Processar evolução mensal
    if (dadosComerciaisHook.evolucao_mensal && Array.isArray(dadosComerciaisHook.evolucao_mensal)) {
      const dadosFormatados = dadosComerciaisHook.evolucao_mensal.map(item => {
        const periodo = getPeriodoMensal(item);
        const metaPeriodo = metasDefinidas[periodo.periodoKey];
        
        // Se estiver em modo consolidado e tiver dados por empresa
        if (isConsolidated && item.empresas) {
          const dadosPorEmpresa = {};
          Object.keys(item.empresas).forEach(empresa => {
            dadosPorEmpresa[empresa] = item.empresas[empresa].total_contratos || 0;
          });
          
          return {
            mes: periodo.mes,
            ano: periodo.ano,
            mesNumero: periodo.mesNumero,
            periodoKey: periodo.periodoKey,
            periodoLabel: periodo.periodoLabel,
            vendas: item.total_contratos || 0,
            receita: item.receita_total || 0,
            receita_instalacao: item.receita_instalacao || 0,
            empresas: dadosPorEmpresa
          };
        } else {
          // Modo individual
          return {
            mes: periodo.mes,
            ano: periodo.ano,
            mesNumero: periodo.mesNumero,
            periodoKey: periodo.periodoKey,
            periodoLabel: periodo.periodoLabel,
            vendas: item.total_contratos || 0,
            meta: metaPeriodo?.vendas || 0,
            receita: item.receita_total || 0,
            receita_instalacao: item.receita_instalacao || 0
          };
        }
      });
      setDadosEvolucao(dadosFormatados);
    } else {
      setDadosEvolucao([]);
    }

    setUltimaAtualizacao(new Date());
  }, [dadosComerciaisHook, getPeriodoMensal, metasDefinidas, isConsolidated]);


  // Atualizar dados de evolução quando metas mudarem
  useEffect(() => {
    if (dadosComerciaisHook?.evolucao_mensal) {
    setDadosEvolucao(prev => prev.map(item => ({
      ...item,
      meta: getMetaPeriodo(item)?.vendas || 0
    })));
    }
  }, [dadosComerciaisHook?.evolucao_mensal, getMetaPeriodo, metasDefinidas]);


  // Funções do carrossel de gráficos
  const nextChart = () => {
    setCurrentChartIndex((prevIndex) => (prevIndex + 1) % 2); // 2 gráficos
  };

  const prevChart = () => {
    setCurrentChartIndex((prevIndex) => (prevIndex - 1 + 2) % 2);
  };

  const goToChart = (index) => {
    setCurrentChartIndex(index);
  };

  // Auto-play do carrossel de gráficos
  useEffect(() => {
    if (isAutoPlaying) {
      intervalRef.current = setInterval(() => {
        nextChart();
      }, 8000); // 8 segundos para gráficos

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isAutoPlaying]);

  // Pausar auto-play ao interagir
  const handleChartMouseEnter = () => setIsAutoPlaying(false);
  const handleChartMouseLeave = () => setIsAutoPlaying(true);

  // Ref para controlar chamadas simultâneas de metas
  const carregandoMetasRef = useRef(false);

  // Carregar metas comerciais da API
  const carregarMetasComerciais = useCallback(async () => {
    if (!hasComercialFull) return;
    if (carregandoMetasRef.current) return;
    
    carregandoMetasRef.current = true;
    setLoadingMetas(true);
    
    // Limpar metas anteriores antes de carregar novas
    setMetasDefinidas({});
    
    try {
      const anoAtual = new Date().getFullYear();
      const companyContext = getCompanyContext();
      const empresa = companyContext?.companies?.[0]?.name || 'MOV Fibra';
      const anosEvolucao = Array.from(new Set(
        (dadosComerciaisHook?.evolucao_mensal || [])
          .map(item => getPeriodoMensal(item).ano)
          .filter(Boolean)
      ));
      const anosParaBuscar = anosEvolucao.length > 0 ? anosEvolucao : [anoAtual];
      
      const responses = await Promise.all(
        anosParaBuscar.map(ano => metasComerciaisService.buscarMetas(ano, empresa))
      );
      const metasPorAno = responses.reduce((acc, response, index) => {
        if (!response.success) return acc;

        return {
          ...acc,
          ...normalizarMetasPorAno(response.data, anosParaBuscar[index])
        };
      }, {});
      
      if (Object.keys(metasPorAno).length > 0) {
        setMetasDefinidas(metasPorAno);
      } else {
        setMetasDefinidas({});
      }
    } catch (error) {
      console.error('❌ Erro ao carregar metas:', error);
      setMetasDefinidas({});
    } finally {
      setLoadingMetas(false);
      carregandoMetasRef.current = false;
    }
  }, [
    dadosComerciaisHook?.evolucao_mensal,
    getPeriodoMensal,
    hasComercialFull,
    normalizarMetasPorAno
  ]);

  // Processar dados quando o hook retornar dados
  useEffect(() => {
    if (dadosComerciaisHook) {
      processarDadosComerciais();
    }
  }, [dadosComerciaisHook, processarDadosComerciais]);

  // Carregar metas quando empresa muda (apenas modo individual)
  useEffect(() => {
    if (!hasComercialFull) {
      setMetasDefinidas({});
      return;
    }

    if (!isConsolidated) {
      // Limpar metas imediatamente quando empresa muda
      setMetasDefinidas({});
      carregarMetasComerciais();
    } else {
      // Se estiver em modo consolidado, limpar metas
      setMetasDefinidas({});
    }
  }, [carregarMetasComerciais, hasComercialFull, isConsolidated]);

  // Listener para mudanças de empresa
  useEffect(() => {
    const handleCompanyChange = () => {
      if (!hasComercialFull) return;

      atualizarDadosComerciais();
      
      // Carregar metas se não estiver em modo consolidado
      if (!isConsolidated) {
        setMetasDefinidas({});
        carregarMetasComerciais();
      }
    };
    
    window.addEventListener('company-changed', handleCompanyChange);
    
    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [atualizarDadosComerciais, carregarMetasComerciais, hasComercialFull, isConsolidated]);

  // Carregar metas imediatamente quando componente monta (se não consolidado)
  useEffect(() => {
    if (hasComercialFull && !isConsolidated) {
      carregarMetasComerciais();
    }
  }, [carregarMetasComerciais, hasComercialFull, isConsolidated]); // Executa quando isConsolidated muda

  // Recarregar metas quando a evolução mensal indicar anos diferentes do ano atual
  useEffect(() => {
    if (hasComercialFull && !isConsolidated && dadosComerciaisHook?.evolucao_mensal?.length) {
      carregarMetasComerciais();
    }
  }, [carregarMetasComerciais, dadosComerciaisHook?.evolucao_mensal, hasComercialFull, isConsolidated]);

  // Forçar aba para dashboard quando estiver em modo consolidado
  useEffect(() => {
    if (isConsolidated && abaAtiva === 'metas') {
      setAbaAtiva('dashboard');
    }
  }, [isConsolidated, abaAtiva]);

  // Verificar se usuário pode definir metas
  const podeDefinirMetas = () => {
    
    // Verificar se tem permissão de edição de metas comerciais
    if (user?.permissions) {
      const hasPermission = user.permissions.some(p => p.name === 'comercial.metas.edit');
      return hasPermission;
    }
    
    // Fallback para verificação por role (para compatibilidade)
    const roleName = user?.role?.name?.toLowerCase();
    const hasRole = roleName === 'admin' || roleName === 'manager';
    return hasRole;
  };

  const carregarVendedoresParaMetas = async () => {
    setLoadingVendedoresMetas(true);

    try {
      const companyContextAtual = getCompanyContext();
      const empresa = companyContextAtual?.companies?.[0]?.name || 'MOV Fibra';
      const response = await metasComerciaisService.listarVendedores(empresa);

      setVendedoresDisponiveisMetas(response.success ? response.data : []);
    } catch (error) {
      console.error('❌ Erro ao carregar vendedores para metas:', error);
      setVendedoresDisponiveisMetas([]);
    } finally {
      setLoadingVendedoresMetas(false);
    }
  };

  // Abrir modal para definir meta
  const abrirModalMeta = (periodoMeta) => {
    if (!podeDefinirMetas()) {
      alert('Apenas CEO/Conselho podem definir metas');
      return;
    }
    const periodo = typeof periodoMeta === 'string'
      ? getPeriodoMensal({ mes: periodoMeta })
      : periodoMeta;
    const mes = periodo?.mes || 'N/A';
    const ano = periodo?.ano || new Date().getFullYear();
    const periodoKey = periodo?.periodoKey || `${ano}-${mes}`;

    setMesEdicao(mes);
    setAnoEdicao(ano);
    setPeriodoEdicaoLabel(periodo?.periodoLabel || `${String(mes).toUpperCase()}/${ano}`);
    const metaAtual = metasDefinidas[periodoKey] || (!periodo?.periodoKey ? metasDefinidas[mes] : null);
    setMetaTemp(metaAtual ? metaAtual.vendas.toString() : '');
    setMetasVendedoresTemp(normalizarMetasVendedores(metaAtual?.vendedores || []));
    setModalMetaAberto(true);
    carregarVendedoresParaMetas();
  };

  // Salvar meta
  const salvarMeta = async () => {
    if (metaTemp === '' || isNaN(metaTemp)) {
      alert('Digite um valor válido para a meta');
      return;
    }

    const metaGeral = parseInt(metaTemp, 10);
    const metasIndividuais = metasVendedoresTemp.map((item) => ({
      vendedor_id: parseInt(item.vendedor_id, 10),
      vendedor_nome: String(item.vendedor_nome || '').trim(),
      meta_vendas: parseInt(item.meta_vendas, 10),
    }));
    const possuiLinhaIncompleta = metasIndividuais.some((item) => (
      !item.vendedor_id || !item.vendedor_nome || Number.isNaN(item.meta_vendas) || item.meta_vendas < 0
    ));
    const ids = metasIndividuais.map((item) => item.vendedor_id);
    const possuiDuplicado = ids.length !== new Set(ids).size;
    const totalMetasVendedores = metasIndividuais.reduce((sum, item) => sum + item.meta_vendas, 0);

    if (possuiLinhaIncompleta) {
      alert('Preencha todos os vendedores adicionados ou remova as linhas incompletas.');
      return;
    }

    if (possuiDuplicado) {
      alert('Não é permitido repetir o mesmo vendedor na meta do mês.');
      return;
    }

    if (totalMetasVendedores > metaGeral) {
      alert('A soma das metas dos vendedores não pode ser maior que a meta geral.');
      return;
    }

    try {
      const periodoKey = `${anoEdicao}-${mesEdicao}`;
      const dadosMeta = {
        ano: anoEdicao,
        mes: mesEdicao,
        meta_vendas: metaGeral,
        meta_receita: metaGeral * 1000, // Estimativa de receita baseada em vendas
        criado_por: user.name || user.email,
        vendedores: metasIndividuais,
      };

      const response = await metasComerciaisService.salvarMeta(dadosMeta);

      if (response.success) {
        const metaAtualizada = response.data || {
          vendas: metaGeral,
          receita: metaGeral * 1000,
          vendedores: metasIndividuais,
          total_meta_vendedores: totalMetasVendedores,
          saldo_disponivel: Math.max(0, metaGeral - totalMetasVendedores),
        };

        setMetasDefinidas(prev => ({
          ...prev,
          [periodoKey]: metaAtualizada,
          ...(anoEdicao === new Date().getFullYear() ? { [mesEdicao]: metaAtualizada } : {})
        }));

        alert('Meta salva com sucesso!');
        setModalMetaAberto(false);
        setMetaTemp('');
        setMesEdicao('');
        setPeriodoEdicaoLabel('');
        setMetasVendedoresTemp([]);
      } else {
        alert(`Erro ao salvar meta: ${response.error}`);
      }
    } catch (error) {
      console.error('❌ Erro ao salvar meta:', error);
      alert('Erro ao salvar meta. Tente novamente.');
    }
  };

  // Cancelar edição de meta
  const cancelarMeta = () => {
    setModalMetaAberto(false);
    setMetaTemp('');
    setMesEdicao('');
    setPeriodoEdicaoLabel('');
    setMetasVendedoresTemp([]);
  };

  const handleRecarregar = useCallback(() => {
    if (hasComercialFull) {
      atualizarDadosComerciais();
    }
  }, [atualizarDadosComerciais, hasComercialFull]);

  // Função para atualizar quando filtro de data muda
  const handleFiltroDataChange = (novoFiltro) => {
    setFiltroData(novoFiltro);
    // Os dados serão recarregados automaticamente pelo hook
  };


  // Funções comentadas para remover warnings de variáveis não utilizadas
  /*
  const buscarDadosVendas = async () => {
    try {
      // Usar o endpoint da API para dados de contratos
      const result = await api.get('/contratos', {
        params: {
          empresa: typeof user.company === 'string' ? user.company : user.company?.name || 'N/A',
          data_inicio: '2025-01-01',
          data_fim: '2025-06-30'
      });
      return result;
    } catch (error) {
      console.error('❌ Erro ao buscar dados de vendas:', error);
      return { success: false, data: [] };
    }
  };

  const calcularMetricasComerciais = (contratos) => {
    const mesAtual = new Date().getMonth() + 1;
    const anoAtual = new Date().getFullYear();
    
    // Filtrar contratos do mês atual
    const contratosDoMes = contratos.filter(contrato => {
      const dataContrato = new Date(contrato.data_inicio);
      return dataContrato.getMonth() + 1 === mesAtual && dataContrato.getFullYear() === anoAtual;
    });

    const totalContratos = contratosDoMes.length;
    const receitaTotal = contratosDoMes.reduce((sum, c) => sum + (parseFloat(c.valor_mensalidade) || 0), 0);
    const ticketMedio = totalContratos > 0 ? receitaTotal / totalContratos : 0;
    
    // Meta fictícia para cálculo de performance
    const metaMensal = 250;
    const metaReceita = 500000;
    
    const performanceMeta = totalContratos > 0 ? (totalContratos / metaMensal) * 100 : 0;
    const performanceReceita = receitaTotal > 0 ? (receitaTotal / metaReceita) * 100 : 0;

    return {
      totalVendas: totalContratos,
      projecaoTotalVendas: Math.round(totalContratos * 1.2), // Projeção 20% maior
      totalReceitaVendas: receitaTotal,
      projecaoReceitaVendas: Math.round(receitaTotal * 1.2), // Projeção 20% maior
      ticketMedio,
      performanceMeta: Math.round(performanceMeta * 10) / 10,
      performanceReceita: Math.round(performanceReceita * 10) / 10,
      novosClientes: totalContratos,
      conversao: Math.round(Math.random() * 20 + 80) // Conversão simulada
    };
  };

  const processarVendedores = (contratos) => {
    const vendedoresMap = {};
    
    contratos.forEach(contrato => {
      const vendedor = contrato.vendedor || 'Não informado';
      if (!vendedoresMap[vendedor]) {
        vendedoresMap[vendedor] = {
          nome: vendedor,
          contratos: 0,
          receita: 0
        };
      }
      vendedoresMap[vendedor].contratos++;
      vendedoresMap[vendedor].receita += parseFloat(contrato.valor_mensalidade) || 0;
    });

    return Object.values(vendedoresMap)
      .sort((a, b) => b.receita - a.receita)
      .slice(0, 5);
  };

  const filtrarNovosClientes = (contratos) => {
    const mesAtual = new Date().getMonth() + 1;
    const anoAtual = new Date().getFullYear();
    
    return contratos
      .filter(contrato => {
        const dataContrato = new Date(contrato.data_inicio);
        return dataContrato.getMonth() + 1 === mesAtual && dataContrato.getFullYear() === anoAtual;
      })
      .slice(0, 10)
      .map(contrato => ({
        nome: contrato.cliente,
        valor: parseFloat(contrato.valor_mensalidade) || 0,
        data: contrato.data_inicio,
        cidade: contrato.cidade,
        telefone: contrato.telefone,
        email: contrato.email
      }));
  };

  const getDadosComercaisMock = () => ({
    totalVendas: 337,
    projecaoTotalVendas: 337,
    totalReceitaVendas: 41825.31,
    projecaoReceitaVendas: 59750.44,
    ticketMedio: 124.11,
    performanceMeta: 142,
    performanceReceita: 134,
    novosClientes: 337,
    conversao: 89.4
  });

  const getVendedoresMock = () => [
    { nome: 'Carlos Silva', contratos: 47, receita: 8940 },
    { nome: 'Ana Costa', contratos: 42, receita: 6720 },
    { nome: 'Roberto Lima', contratos: 38, receita: 5890 },
    { nome: 'Maria Santos', contratos: 35, receita: 5250 },
    { nome: 'João Pereira', contratos: 33, receita: 4980 }
  ];
  */

  // Debug removido para evitar spam no console

  if (hasMinhasVendasOnly) {
    return (
      <>
        <CompanyChangeBlur />
        <PageLayout showRefreshButton={false}>
          <main className="w-full px-3 py-4 sm:px-5 lg:px-8 lg:py-6">
            {empresaConsultada && (
              <div className="mb-5 flex flex-wrap gap-1 rounded-lg bg-[color:var(--surface-muted)] p-1 sm:inline-flex">
                {[
                  ['vendas', 'Minhas vendas', ReceiptText],
                  ['consulta-interempresa', `Consultar cliente na ${empresaConsultada}`, UserSearch],
                ].map(([id, rotulo, Icone]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setAbaDaVendedora(id)}
                    aria-current={abaDaVendedora === id ? 'page' : undefined}
                    className={`inline-flex min-h-11 items-center gap-1.5 rounded-md px-4 text-sm font-medium transition-colors ${
                      abaDaVendedora === id
                        ? 'bg-[color:var(--surface)] text-[color:var(--accent)] shadow-sm'
                        : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                    }`}
                  >
                    <Icone className="h-4 w-4" aria-hidden="true" />
                    {rotulo}
                  </button>
                ))}
              </div>
            )}
            {abaDaVendedora === 'vendas' || !empresaConsultada
              ? <MinhasVendasTab />
              : <ConsultaXt empresaConsultada={empresaConsultada} />}
          </main>
        </PageLayout>
      </>
    );
  }

  if (hasRankingOnly) {
    return (
      <>
        <CompanyChangeBlur />
        <PageLayout showRefreshButton={false}>
          <main className="w-full px-3 py-4 sm:px-5 lg:px-8 lg:py-6">
            <div className="mb-4 flex flex-col gap-3 rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)]/80 p-3 shadow-sm backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:p-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--text-muted)]">
                  Acesso exclusivo
                </p>
                <h1 className="mt-1 text-2xl font-black tracking-tight text-[color:var(--text-primary)]">
                  Ranking Comercial
                </h1>
              </div>

              <div className="sm:min-w-[220px]">
                <DateRangeFilterBlur
                  value={filtroData}
                  loading={false}
                  onChange={handleFiltroDataChange}
                />
              </div>
            </div>

            <RankingComercialTab
              filtroData={filtroData}
              companyContext={companyContext}
              fullscreen
            />
          </main>
        </PageLayout>
      </>
    );
  }

  // Loading inicial da página
  if (initialLoading) {
    return <LoadingSpinner />;
  }

  if (errorComercial) {
    return (
      <PageLayout 
        showRefreshButton={true}
        customRefreshFunction={handleRecarregar}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          
          <div className="bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-[color:var(--status-warn)]" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-[color:var(--status-warn)]">
                  Serviço temporariamente indisponível
                </h3>
                <div className="mt-2 text-sm text-[color:var(--status-warn)]">
                  <p>
                    Os dados comerciais para esta empresa não estão disponíveis no momento. 
                    Isso pode acontecer quando a rota N8N específica ainda não está configurada.
                  </p>
                  <p className="mt-2">
                    <strong>Dica:</strong> Tente trocar para outra empresa ou volte mais tarde.
                  </p>
                </div>
                <div className="mt-4">
                  <div className="flex space-x-3">
                    <button
                      onClick={handleRecarregar}
                      className="bg-[color:var(--surface-muted)] text-[color:var(--status-warn)] px-3 py-2 rounded-md text-sm font-medium hover:bg-[color:var(--surface)] transition-colors"
                    >
                      Tentar novamente
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Mostrar dados zerados para permitir navegação */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-[color:var(--surface)] rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-[color:var(--surface-muted)] rounded-full flex items-center justify-center">
                    <span className="text-[color:var(--text-muted)] text-sm">-</span>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-[color:var(--text-muted)]">Total de Vendas</p>
                  <p className="text-2xl font-semibold text-[color:var(--text-muted)]">0</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[color:var(--surface)] rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-[color:var(--surface-muted)] rounded-full flex items-center justify-center">
                    <span className="text-[color:var(--text-muted)] text-sm">-</span>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-[color:var(--text-muted)]">Receita Total</p>
                  <p className="text-2xl font-semibold text-[color:var(--text-muted)]">R$ 0,00</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[color:var(--surface)] rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-[color:var(--surface-muted)] rounded-full flex items-center justify-center">
                    <span className="text-[color:var(--text-muted)] text-sm">-</span>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-[color:var(--text-muted)]">Ticket Médio</p>
                  <p className="text-2xl font-semibold text-[color:var(--text-muted)]">R$ 0,00</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[color:var(--surface)] rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-[color:var(--surface-muted)] rounded-full flex items-center justify-center">
                    <span className="text-[color:var(--text-muted)] text-sm">-</span>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-[color:var(--text-muted)]">Clientes com OS >72h</p>
                  <p className="text-2xl font-semibold text-[color:var(--text-muted)]">0</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-[color:var(--surface)] rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-[color:var(--text-primary)] mb-4">Informações</h3>
            <p className="text-[color:var(--text-muted)]">
              Os dados comerciais para esta empresa não estão disponíveis no momento. 
              Isso pode acontecer quando a rota N8N específica ainda não está configurada.
            </p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <>
      <CompanyChangeBlur />
      <PageLayout 
        showRefreshButton={true}
        customRefreshFunction={handleRecarregar}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Header com Abas e Filtro integrados */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Abas */}
            <div className="flex flex-wrap gap-1 bg-[color:var(--surface-muted)] p-1 rounded-lg">
              <button
                onClick={() => setAbaAtiva('dashboard')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                  abaAtiva === 'dashboard'
                    ? 'bg-[color:var(--surface)] text-[color:var(--accent)] shadow-sm'
                    : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                }`}
              >
                <LayoutDashboard className="h-3.5 w-3.5" />
                Dashboard
              </button>
              <button
                onClick={() => setAbaAtiva('ranking')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                  abaAtiva === 'ranking'
                    ? 'bg-[color:var(--surface)] text-[color:var(--accent)] shadow-sm'
                    : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                }`}
              >
                <Trophy className="h-3.5 w-3.5" />
                Ranking
              </button>
              {/* A aba permanece com a régua ativa, em leitura: é a única tela que mostra o
                  histórico de metas anteriores à vigência, e ele continua válido. */}
              {!isConsolidated && (
                <button
                  onClick={() => setAbaAtiva('metas')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                    abaAtiva === 'metas'
                      ? 'bg-[color:var(--surface)] text-[color:var(--accent)] shadow-sm'
                      : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                  }`}
                >
                  <Target className="h-3.5 w-3.5" />
                  Metas
                  {podeDefinirMetas() && (
                    <Settings className="h-3 w-3" />
                  )}
                </button>
              )}
              <button
                onClick={() => setAbaAtiva('comissao')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                  abaAtiva === 'comissao'
                    ? 'bg-[color:var(--surface)] text-[color:var(--accent)] shadow-sm'
                    : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                }`}
              >
                <DollarSign className="h-3.5 w-3.5" />
                Comissão
              </button>
              {!isConsolidated && (
                <button
                  onClick={() => setAbaAtiva('minhas-vendas')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                    abaAtiva === 'minhas-vendas'
                      ? 'bg-[color:var(--surface)] text-[color:var(--accent)] shadow-sm'
                      : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                  }`}
                >
                  <ReceiptText className="h-3.5 w-3.5" />
                  Vendas por vendedor
                </button>
              )}
              {empresaConsultada && (
                <button
                  onClick={() => setAbaAtiva('consulta-interempresa')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                    abaAtiva === 'consulta-interempresa'
                      ? 'bg-[color:var(--surface)] text-[color:var(--accent)] shadow-sm'
                      : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                  }`}
                >
                  <UserSearch className="h-3.5 w-3.5" />
                  Consulta {empresaConsultada}
                </button>
              )}
            </div>

            {/* Filtro de Datas — oculto na Comissão sob a régua; ver `mostrarFiltroPeriodo`. */}
            {mostrarFiltroPeriodo && (
              <div className="flex-shrink-0">
                <DateRangeFilterBlur
                  value={filtroData}
                  loading={loadingComercial}
                  onChange={handleFiltroDataChange}
                />
              </div>
            )}
          </div>
        </div>

        {/* Conteúdo baseado na aba ativa */}
        {abaAtiva === 'dashboard' && (
          <>
            {/* Cards de Métricas Principais */}
            <MetricasComerciais dadosComerciais={dadosComerciais} />

            
             
             {/* Gráficos e Análises */}
             <div className="mb-8">
               {/* Mobile: Carrossel de gráficos */}
               <div className="block lg:hidden">
                 <div 
                   className="relative"
                   onMouseEnter={handleChartMouseEnter}
                   onMouseLeave={handleChartMouseLeave}
                 >
                   {/* Container do carrossel */}
                   <div className="overflow-hidden">
                     <div 
                       className="flex transition-transform duration-500 ease-in-out"
                       style={{ transform: `translateX(-${currentChartIndex * 100}%)` }}
                     >
                       {/* Gráfico 1: Evolução de Vendas */}
                       <div className="w-full flex-shrink-0">
                         {isConsolidated ? (
                           <GraficoEvolucaoConsolidado
                             dadosEvolucao={dadosEvolucao}
                             empresas={dadosComerciaisHook?.empresas || []}
                           />
                         ) : (
                           <GraficoEvolucaoVendas
                             dadosEvolucao={dadosEvolucao}
                             metasDefinidas={metasDefinidas}
                             podeDefinirMetas={podeDefinirMetas()}
                             onDefinirMetas={() => setAbaAtiva('metas')}
                           />
                         )}
                       </div>
                       
                       {/* Gráfico 2: Evolução da Receita */}
                       <div className="w-full flex-shrink-0">
                         <GraficoReceita 
                           dadosReceita={dadosEvolucao}
                         />
                       </div>
                     </div>
                   </div>

                   {/* Controles de navegação */}
                   <button
                     onClick={prevChart}
                     className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-[color:var(--surface)]/90 hover:bg-[color:var(--surface)] rounded-full p-2 shadow-lg transition-all z-10"
                   >
                     <ChevronLeft className="h-5 w-5 text-[color:var(--text-muted)]" />
                   </button>
                   <button
                     onClick={nextChart}
                     className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[color:var(--surface)]/90 hover:bg-[color:var(--surface)] rounded-full p-2 shadow-lg transition-all z-10"
                   >
                     <ChevronRight className="h-5 w-5 text-[color:var(--text-muted)]" />
                   </button>

                   {/* Indicadores */}
                   <div className="flex justify-center mt-4 space-x-2 items-center">
                     <button
                       onClick={() => goToChart(0)}
                       aria-label="Gráfico 1"
                       className={`rounded-full transition-all duration-300 ${
                         currentChartIndex === 0
                           ? 'w-5 h-3 bg-[color:var(--accent)]'
                           : 'w-3 h-3 bg-[color:var(--border)] hover:bg-[color:var(--text-muted)]'
                       }`}
                     />
                     <button
                       onClick={() => goToChart(1)}
                       aria-label="Gráfico 2"
                       className={`rounded-full transition-all duration-300 ${
                         currentChartIndex === 1
                           ? 'w-5 h-3 bg-[color:var(--accent)]'
                           : 'w-3 h-3 bg-[color:var(--border)] hover:bg-[color:var(--text-muted)]'
                       }`}
                     />
                   </div>
                 </div>
               </div>

               {/* Desktop: Grid lado a lado */}
               <div className="hidden lg:grid grid-cols-2 gap-6">
                 {isConsolidated ? (
                   <div className="col-span-2">
                     <GraficoEvolucaoConsolidado
                       dadosEvolucao={dadosEvolucao}
                       empresas={dadosComerciaisHook?.empresas || []}
                     />
                   </div>
                 ) : (
                   <>
                     <GraficoEvolucaoVendas
                       dadosEvolucao={dadosEvolucao}
                       metasDefinidas={metasDefinidas}
                       podeDefinirMetas={podeDefinirMetas()}
                       onDefinirMetas={() => setAbaAtiva('metas')}
                     />
                     <GraficoReceita 
                        dadosReceita={dadosEvolucao}
                      />
                   </>
                 )}
               </div>
             </div>

            {/* Meta do dia da equipe interna: uma empresa por vez, como a meta. */}
            {!isConsolidated && <RitmoDaEquipe />}

            {/* Seção de Rankings e Listas */}
            <div className="mb-8">
              {/* Mobile: Sistema de abas */}
              <div className="block xl:hidden">
                {/* Navegação das abas */}
                <div className="flex bg-[color:var(--surface-muted)] rounded-lg p-1 mb-4">
                  <button
                    onClick={() => setActiveTab('vendedores')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      activeTab === 'vendedores'
                        ? 'bg-[color:var(--surface)] text-[color:var(--accent)] shadow-sm'
                        : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                    }`}
                  >
                    Inadimplência por vendedor
                  </button>
                  <button
                    onClick={() => setActiveTab('clientes')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      activeTab === 'clientes'
                        ? 'bg-[color:var(--surface)] text-[color:var(--accent)] shadow-sm'
                        : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                    }`}
                  >
                    Clientes com OS >72h
                  </button>
                </div>

                {/* Conteúdo das abas */}
                <div className="min-h-[400px]">
                  {activeTab === 'vendedores' && (
                    <ListaInadimplenciaVendedores vendedores={listaInadimplencia} />
                  )}
                  {activeTab === 'clientes' && (
                    <ListaClientesOsAberta72h
                      clientes={clientesOsAberta72h}
                      clientesAssinatura={clientesAssinatura}
                    />
                  )}
                </div>
              </div>

              {/* Desktop: Grid lado a lado */}
              <div className="hidden xl:grid grid-cols-2 gap-6">
                <ListaInadimplenciaVendedores vendedores={listaInadimplencia} />
                <ListaClientesOsAberta72h
                  clientes={clientesOsAberta72h}
                  clientesAssinatura={clientesAssinatura}
                />
              </div>
            </div>
          </>
        )}

        {/* Aba de Metas - só aparece em modo individual */}
        {abaAtiva === 'metas' && !isConsolidated && (
          <GestaoMetas
            inicioVigencia={inicioVigencia}
            dadosEvolucao={dadosEvolucao}
            metasDefinidas={metasDefinidas}
            podeDefinirMetas={podeDefinirMetas()}
            onAbrirModalMeta={abrirModalMeta}
          />
        )}

        {/* Aba de Comissão */}
        {abaAtiva === 'comissao' && (
          <ComissaoTab filtroData={filtroData} />
        )}

        {/* O que cada vendedora vê na subaba dela; aqui o supervisor escolhe quem. */}
        {abaAtiva === 'minhas-vendas' && !isConsolidated && <MinhasVendasTab />}

        {/* Consulta cruzada de CPF ou CNPJ antes da venda. */}
        {abaAtiva === 'consulta-interempresa' && empresaConsultada && (
          <ConsultaXt empresaConsultada={empresaConsultada} />
        )}

        {/* Aba de Ranking */}
        {abaAtiva === 'ranking' && (
          <RankingComercialTab
            filtroData={filtroData}
            companyContext={companyContext}
          />
        )}

        {/* Modal para Definir Meta */}
        <ModalMeta
          isOpen={modalMetaAberto}
          mesEdicao={mesEdicao}
          periodoLabel={periodoEdicaoLabel}
          metaTemp={metaTemp}
          vendasAtuais={dadosEvolucao.find(item => item.periodoKey === `${anoEdicao}-${mesEdicao}`)?.vendas || 0}
          onMetaChange={(e) => setMetaTemp(e.target.value)}
          onSalvar={salvarMeta}
          onCancelar={cancelarMeta}
          vendedoresDisponiveis={vendedoresDisponiveisMetas}
          loadingVendedores={loadingVendedoresMetas}
          metasVendedores={metasVendedoresTemp}
          onMetasVendedoresChange={setMetasVendedoresTemp}
        />
        </div>
      </PageLayout>
    </>
  );
};

export default Comercial; 
